import { useAvailableTimestamps } from '@cannect/hooks/useAvailableTimestamps'
import { Button, Typography } from '@cannect/new-components/atoms'
import { DatePickerField, SelectField, SwitchField } from '@cannect/new-components/molecules'
import {
  useGetAvailableDates,
  useGetExpertisesFiltersOptions,
  useGetPathologiesFiltersOptions,
  useGetPrescribersFiltersOptions
} from '@cannect/services/resources/scheduling'
import { formatDate } from '@cannect/utils/date'
import { useScheduling } from 'hooks/useScheduling'
import { SearchIcon, XCircle } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router'

export type FilterBy = 'expertise' | 'pathology' | 'prescriber'

type FilterFormProps = {
  isLoading: boolean
  showFilterByDate?: boolean
}

/**
 * `serializeOptions` devolve `value` como número (o id da tabela), mas o que vem da URL é sempre
 * string — e o `SelectField` casa a option com `===`. Sem a conversão o filtro busca certo e o
 * campo aparece vazio, inclusive depois de o próprio "Buscar" reescrever a query.
 */
const toOptionValue = (value: string) => (value !== '' && !Number.isNaN(Number(value)) ? Number(value) : value)

const normalizeName = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

export default function FilterForm({ isLoading, showFilterByDate = false }: FilterFormProps) {
  const history = useHistory()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const { setValue, watch, control } = useFormContext()
  const [filterBy, setFilterBy] = useState<FilterBy>('expertise')
  const [filterById, setFilterById] = useState<string | null>(null)

  const { resetContextValues, setSelectedPrescriber } = useScheduling()

  const currentFilter = watch('filter')
  const searchDate = watch('search_date')
  const attendsChildren = watch('attends_children')

  const currentFilterBy = {
    type: filterBy,
    value: filterById
  }

  const { data: expertisesList, isLoading: isLoadingExpertises } = useGetExpertisesFiltersOptions()
  const { data: pathologiesList, isLoading: isLoadingPathologies } = useGetPathologiesFiltersOptions()
  const { data: prescribersList, isLoading: isLoadingPrescribers } = useGetPrescribersFiltersOptions()
  const { data: availableDatesState, isLoading: isLoadingAvailableDates } = useGetAvailableDates({
    filterBy: currentFilterBy
  })

  const { isDateDisabled } = useAvailableTimestamps(availableDatesState?.available_date_times)

  const isLoadingLists = useMemo(
    () => isLoadingExpertises || isLoadingPathologies || isLoadingPrescribers,
    [isLoadingExpertises, isLoadingPathologies, isLoadingPrescribers]
  )

  const resetValues = () => {
    setValue('scheduling_date', null)
    setValue('scheduling_time', '')
    setValue('prescriber', null)
    setSelectedPrescriber(null)
    resetContextValues()
  }

  const handleChangeFilterType = (filter: FilterBy) => {
    setFilterBy(filter)
    setFilterById(null)
    setValue('search_date', null)
    resetValues()
  }

  const handleSelectFilter = (id: string) => {
    setFilterById(id)
  }

  const handleDateChange = (date: Date | string | null) => {
    setValue('search_date', date)
  }

  const handleSearch = () => {
    if (!filterById && !attendsChildren) return

    if (filterById) {
      params.set('filter_by', filterBy)
      params.set('filter_by_id', filterById)
    } else {
      params.delete('filter_by')
      params.delete('filter_by_id')
    }

    if (attendsChildren) {
      params.set('attends_children', 'true')
    } else {
      params.delete('attends_children')
    }

    if (searchDate) {
      params.set('searchDate', formatDate(searchDate, 'yyyy-MM-dd'))
    }

    history.push({ search: params.toString() })
  }

  const handleClearDate = () => {
    setValue('search_date', null)
    params.delete('searchDate')
    history.push({ search: params.toString() })
  }

  const selectFilters = {
    expertise: {
      id: 'expertise-filter',
      placeholder: 'Selecione a especialidade',
      options: expertisesList || []
    },
    pathology: {
      id: 'pathology-filter',
      placeholder: 'Selecione o sintoma',
      options: pathologiesList || []
    },
    prescriber: {
      id: 'prescriber-filter',
      placeholder: 'Selecione o profissional',
      options: prescribersList || []
    }
  }

  useEffect(() => {
    const filterByParam = params.get('filter_by') as FilterBy
    const filterByIdParam = params.get('filter_by_id')
    const searchDateParam = params.get('searchDate')
    const attendsChildrenParam = params.get('attends_children') === 'true'

    setValue('attends_children', attendsChildrenParam)

    // Deixa o botão do tipo certo já ativo enquanto o nome ainda está sendo resolvido em id.
    if (filterByParam && selectFilters[filterByParam]) {
      setFilterBy(filterByParam)
    }

    if (filterByParam && filterByIdParam) {
      setFilterBy(filterByParam)
      setFilterById(filterByIdParam)
      setValue('filter', toOptionValue(filterByIdParam))

      if (searchDateParam) {
        setValue('search_date', new Date(`${searchDateParam}T00:00:00-03:00`))
      }
    } else {
      setFilterById(null)
      setValue('filter', null)
      setValue('search_date', null)
    }
  }, [location.search])

  /**
   * `filter_by_name` é a porta de entrada de quem linka para cá de fora (hoje as condições do hero
   * da home). Vem o nome da patologia/especialidade em vez do id porque as tabelas são legadas, os
   * ids não têm garantia de bater entre ambientes e as listas são filtradas por disponibilidade —
   * quem sabe o id é a lista que a API acabou de devolver, não o link.
   *
   * O parâmetro pode repetir, em ordem de preferência: usa-se o primeiro nome que existir na lista
   * daquele ambiente. Resolvido, a URL vira a canônica (`filter_by_id`), que é a que o
   * `FilterByStep` lê para buscar os profissionais. Nenhum nome casando, o filtro sai da URL e a
   * tela abre sem filtro, em vez de ficar com uma lista vazia sem explicação.
   */
  useEffect(() => {
    const filterByNameParams = params.getAll('filter_by_name')

    if (filterByNameParams.length === 0 || isLoadingLists) return

    const filterByParam = (params.get('filter_by') as FilterBy) || 'expertise'
    const options = selectFilters[filterByParam]?.options ?? []
    const matchedOption = filterByNameParams.reduce<(typeof options)[number] | undefined>(
      (found, name) => found ?? options.find((option) => normalizeName(String(option.label)) === normalizeName(name)),
      undefined
    )

    params.delete('filter_by_name')

    if (matchedOption) {
      params.set('filter_by', filterByParam)
      params.set('filter_by_id', String(matchedOption.value))
    } else {
      params.delete('filter_by')
      params.delete('filter_by_id')
    }

    history.replace({ search: params.toString() })
  }, [location.search, isLoadingLists, expertisesList, pathologiesList, prescribersList])

  useEffect(() => {
    const searchDateParam = params.get('searchDate')
    const date = searchDateParam ? new Date(`${searchDateParam}T00:00:00-03:00`) : null

    if (date && availableDatesState && isDateDisabled(date)) {
      setValue('search_date', null)
      params.delete('searchDate')
      history.push({ search: params.toString() })
    }
  }, [availableDatesState, location.search])

  const currentSelectedFilter = selectFilters[filterBy]

  return (
    <div className="mt-25 w-full rounded bg-neutral-50 bg-opacity-50 p-1 backdrop-blur-[64px]">
      <div className="grid gap-2 rounded bg-white p-1">
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 grid grid-cols-2 items-center gap-2 lg:col-span-1 lg:grid-cols-3">
            <Button
              disabled={isLoading}
              variant={filterBy === 'expertise' ? 'primary_light' : 'outline_light'}
              onClick={() => handleChangeFilterType('expertise')}
              className="rounded-full border px-8 font-normal transition-all">
              Especialidade
            </Button>
            <Button
              disabled={isLoading}
              variant={filterBy === 'pathology' ? 'primary_light' : 'outline_light'}
              onClick={() => handleChangeFilterType('pathology')}
              className="rounded-full border px-8 font-normal transition-all">
              Sintoma
            </Button>
            <Button
              disabled={isLoading}
              variant={filterBy === 'prescriber' ? 'primary_light' : 'outline_light'}
              onClick={() => handleChangeFilterType('prescriber')}
              className="col-span-2 rounded-full border px-8 font-normal transition-all lg:col-span-1">
              Profissional da saúde
            </Button>
          </div>

          <div className="col-span-2 grid gap-2 lg:col-span-1 lg:grid-cols-[1fr_auto_auto]">
            <SelectField
              menuPortalTarget={document.body}
              isDisabled={isLoading}
              isLoading={isLoadingLists}
              isClearable
              variant="outlined"
              control={control}
              name="filter"
              id={currentSelectedFilter?.id ?? ''}
              onSelectOption={(option: any) => {
                if (option) {
                  handleSelectFilter(option)
                } else {
                  setFilterById(null)
                  setValue('filter', null)
                }
              }}
              placeholder={currentSelectedFilter?.placeholder ?? ''}
              options={currentSelectedFilter?.options ?? []}
            />

            {currentFilter && (
              <div className="grid gap-2 lg:col-span-2">
                {showFilterByDate && (
                  <div className="relative">
                    <DatePickerField
                      calendarProps={{
                        captionLayout: 'buttons',
                        modifiers: {
                          disabled: isDateDisabled
                        },
                        disabled: !currentFilter ||
                          isLoadingAvailableDates || {
                            before: new Date()
                          }
                      }}
                      control={control}
                      name="search_date"
                      onDaySelect={(date) => handleDateChange(date)}
                      inputProps={{
                        placeholder: 'Data (opcional)',
                        variant: 'filledLight',
                        isDisabled: isLoading || !currentFilter,
                        isLoading: isLoadingAvailableDates
                      }}
                    />
                    {searchDate && (
                      <Button
                        size="icon"
                        unstyled
                        onClick={handleClearDate}
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        icon={<XCircle size={16} />}
                      />
                    )}
                  </div>
                )}
                <Button
                  size="icon"
                  onClick={() => handleSearch()}
                  icon={<SearchIcon size={24} />}
                  isLoading={isLoading}
                  disabled={isLoadingLists}>
                  <Typography.Text className="block text-white md:hidden">Buscar</Typography.Text>
                </Button>
              </div>
            )}

            {!currentFilter && (
              <Button
                size="icon"
                onClick={() => handleSearch()}
                icon={<SearchIcon size={24} />}
                isLoading={isLoading}
                disabled={isLoadingLists}>
                <Typography.Text className="block text-white md:hidden">Buscar</Typography.Text>
              </Button>
            )}
          </div>

          <div className="col-span-2 flex items-center px-1">
            <SwitchField
              control={control}
              name="attends_children"
              label="Atende criança"
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
